import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Timer } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const isoToday = () => new Date().toISOString().slice(0, 10);

const REASONS = [
  { v: 'forgot_punches', l: 'Crew forgot to punch in/out' },
  { v: 'missed_clock_out', l: 'Missed clock-out' },
  { v: 'schedule_correction', l: 'Correct to scheduled shift' },
  { v: 'wrong_times', l: 'Wrong times recorded' },
  { v: 'other', l: 'Manual adjustment (other)' },
];

const inputCls = 'w-full mt-1 px-3 py-2 border border-gray-300 dark:border-[#2D3748] rounded-lg bg-white dark:bg-[#1A2433] text-gray-900 dark:text-gray-100 text-sm';

export default function TeamBulkTimeEditDialog({ open, onClose, teamId, teamName, members, token }) {
  const { toast } = useToast();
  const [startDate, setStartDate] = useState(isoToday());
  const [endDate, setEndDate] = useState(isoToday());
  const [mode, setMode] = useState('exact'); // 'exact' | 'duration'
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('19:00');
  const [createMissing, setCreateMissing] = useState(false);
  const [hours, setHours] = useState('8');
  const [reason, setReason] = useState('forgot_punches');
  const [notes, setNotes] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set((members || []).map((m) => m.id)));
      setStartDate(isoToday()); setEndDate(isoToday()); setNotes('');
    }
  }, [open, members]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const submit = async () => {
    if (selected.size === 0) {
      toast({ title: 'Select at least one member', variant: 'destructive' });
      return;
    }
    if (mode === 'duration') {
      const h = parseFloat(hours);
      if (!h || h < 0.25 || h > 24) {
        toast({ title: 'Hours must be between 0.25 and 24', variant: 'destructive' });
        return;
      }
    }
    const label = REASONS.find((r) => r.v === reason)?.l || reason;
    const payload = {
      team_id: teamId,
      start_date: startDate,
      end_date: endDate,
      member_ids: Array.from(selected),
      edit_reason: `[team-bulk-${mode === 'exact' ? 'exact_time' : 'duration'}-${reason}] ${label}${notes ? ` — ${notes}` : ''}`,
    };
    if (mode === 'exact') {
      payload.set_start_time = startTime;
      payload.set_end_time = endTime;
      payload.create_missing = createMissing;
    } else {
      payload.apply_shift_duration = true;
      payload.shift_duration = parseFloat(hours);
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/teams/bulk-edit-time`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Team time updated', description: data.message });
      onClose(true);
    } catch (e) {
      toast({ title: 'Bulk edit failed', description: e.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(false); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-time-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5 text-amber-500" /> Bulk Edit Team Time — {teamName}
          </DialogTitle>
          <DialogDescription>
            Fix a whole crew&apos;s punches in one shot. Every change is audited and reversible from the Payroll Workbench.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold">From date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1" data-testid="bulk-start-date" />
            </div>
            <div>
              <Label className="text-sm font-semibold">To date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1" data-testid="bulk-end-date" />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">What to do</Label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls} data-testid="bulk-mode-select">
              <option value="exact">Set exact start & end times for each day</option>
              <option value="duration">Set each session to a fixed number of hours</option>
            </select>
          </div>

          {mode === 'exact' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold">Shift start</Label>
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1" data-testid="bulk-start-time" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Shift end</Label>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1" data-testid="bulk-end-time" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={createMissing} onCheckedChange={setCreateMissing} data-testid="bulk-create-missing" />
                <span>Create entries for members with no punch that day</span>
              </label>
            </>
          ) : (
            <div>
              <Label className="text-sm font-semibold">Hours per session</Label>
              <Input type="number" min="0.25" max="24" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} className="mt-1" data-testid="bulk-hours-input" />
              <p className="text-xs text-slate-500 mt-1">Each completed session becomes clock-in + this many hours.</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm font-semibold">Members ({selected.size}/{(members || []).length})</Label>
              <div className="flex gap-2 text-xs">
                <button type="button" className="text-amber-600 hover:underline" onClick={() => setSelected(new Set(members.map((m) => m.id)))}>All</button>
                <button type="button" className="text-amber-600 hover:underline" onClick={() => setSelected(new Set())}>None</button>
              </div>
            </div>
            <div className="max-h-36 overflow-y-auto space-y-1 border border-gray-200 dark:border-[#2D3748] rounded-lg p-2">
              {(members || []).map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`bulk-member-${m.id}`}>
                  <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggle(m.id)} />
                  <span className="text-gray-800 dark:text-gray-200">{m.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold">Reason (required for the audit trail)</Label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="bulk-reason-select">
              {REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Crew on-site 7am–7pm, kiosk was offline" className="mt-1" data-testid="bulk-notes-input" />
          </div>

          <div className="flex gap-3 pt-1">
            <Button onClick={submit} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="bulk-submit-btn">
              {saving ? 'Applying…' : 'Apply Bulk Edit'}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => onClose(false)}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
